import { useEffect, useState } from 'react';
import { apiService } from '../services/api';

// ── "≈ N pieces of content" — asked of the server, never computed here ──────
//
// The same credits used to show a different number of pieces depending on the
// screen: the balance floored, plan cards used a typed-in table, the trial and
// packs rounded, and the WordPress plugin divided by a different rate again, so
// 100 credits read as 5 pieces here and 7 in the plugin. The figure now comes
// from ONE server endpoint, GET /credits/estimate, which the plugin also uses.
//
// Every component that shows an estimate asks for its own number. Requests made
// in the same tick are sent together, and answers are cached for the page's
// life, so a grid of plan cards is one request, not one per card.

const cache = new Map();   // credits -> pieces
const inflight = new Map(); // credits -> Promise<pieces|null>
let queue = [];
let flushScheduled = false;

const flush = async () => {
  flushScheduled = false;
  const batch = queue;
  queue = [];
  const values = [...new Set(batch.map((b) => b.credits))];
  try {
    const res = await apiService.getPieceEstimates(values);
    const found = new Map(
      (res?.data?.estimates || []).map((e) => [Number(e.credits), Number(e.pieces)]),
    );
    batch.forEach(({ credits, resolve }) => {
      const pieces = found.has(credits) && Number.isFinite(found.get(credits))
        ? found.get(credits)
        : null;
      if (pieces !== null) cache.set(credits, pieces);
      inflight.delete(credits);
      resolve(pieces);
    });
  } catch {
    // No number rather than a locally guessed one: a guess is exactly how the
    // screens disagreed. Callers drop the line while this is null.
    batch.forEach(({ credits, resolve }) => {
      inflight.delete(credits);
      resolve(null);
    });
  }
};

/** Pieces for a credit amount, from the server. Resolves null on failure. */
export function fetchPieceEstimate(credits) {
  const c = Number(credits);
  if (!Number.isFinite(c) || c < 0) return Promise.resolve(null);
  if (cache.has(c)) return Promise.resolve(cache.get(c));
  if (inflight.has(c)) return inflight.get(c);

  const promise = new Promise((resolve) => { queue.push({ credits: c, resolve }); });
  inflight.set(c, promise);
  if (!flushScheduled) {
    flushScheduled = true;
    Promise.resolve().then(flush);
  }
  return promise;
}

/** Test hook: forget every cached answer. */
export function resetPieceEstimates() {
  cache.clear();
  inflight.clear();
  queue = [];
  flushScheduled = false;
}

/**
 * How many pieces of content `credits` buys, or null while unknown.
 *
 * @param {number|null|undefined} credits  null/undefined (e.g. a balance still
 *   loading) asks nothing and returns null.
 */
export function usePieceEstimate(credits) {
  const c = credits === null || credits === undefined ? NaN : Number(credits);
  const valid = Number.isFinite(c) && c >= 0;
  const [pieces, setPieces] = useState(() => (valid && cache.has(c) ? cache.get(c) : null));

  useEffect(() => {
    if (!valid) { setPieces(null); return undefined; }
    if (cache.has(c)) { setPieces(cache.get(c)); return undefined; }
    let active = true;
    setPieces(null);
    fetchPieceEstimate(c).then((p) => { if (active) setPieces(p); });
    return () => { active = false; };
  }, [c, valid]);

  return pieces;
}
